// Stat tiles and the storage bar for the tenant summary. Display only: the page maps the API response to
// these props (src/lib/summary.ts), so a change in field names does not touch this file.
import { formatBytes } from "../lib/format";

export interface StatTile {
  /** Short label under the number, for example "Namespaces". */
  label: string;
  /** The number, formatted for display, for example "1,234" or "1.2 GiB". */
  value: string;
  /** Optional second line, for example "2 pending invites". */
  note?: string;
}

export interface StorageUse {
  usedBytes: number;
  /** Null when the plan has no storage quota. */
  quotaBytes: number | null;
}

/** Percent of the quota used, from 0 to 100, rounded down. Null without a quota. */
export function storagePercent(s: StorageUse): number | null {
  if (s.quotaBytes === null || !(s.quotaBytes > 0)) return null;
  return Math.min(100, Math.floor((Math.max(0, s.usedBytes) / s.quotaBytes) * 100));
}

export function SummaryTiles({ tiles, storage }: { tiles: StatTile[]; storage: StorageUse }) {
  const pct = storagePercent(storage);
  const level = pct === null ? "none" : pct >= 100 ? "full" : pct >= 80 ? "high" : "ok";
  return (
    <section className="summary" aria-label="Tenant summary">
      <ul className="tiles" role="list">
        {tiles.map((t) => (
          <li key={t.label} className="tile">
            <span className="tile-value">{t.value}</span>
            <span className="tile-label">{t.label}</span>
            {t.note && <span className="tile-note">{t.note}</span>}
          </li>
        ))}
      </ul>
      <div className="storage" data-level={level}>
        <p className="storage-text">
          <span>Storage</span>
          <span className="mono">
            {formatBytes(storage.usedBytes)}
            {storage.quotaBytes !== null && <> of {formatBytes(storage.quotaBytes)}</>}
            {pct !== null && <> ({pct} %)</>}
          </span>
        </p>
        {pct !== null && (
          <div
            className="storage-bar"
            role="meter"
            aria-label="Storage used"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-valuetext={`${formatBytes(storage.usedBytes)} of ${formatBytes(storage.quotaBytes!)}`}
          >
            <span className="storage-fill" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </section>
  );
}
